import React, { useState, useEffect } from 'react';
import {
  Home, CalendarDays, BookOpen, Users, UserRound, Mail, HelpCircle, Box, PackagePlus, PieChart, Settings, ChevronLeft, ChevronRight
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

function MenuCategory({ title, collapsed }) {
  if (collapsed) return <div className="h-4" />; 
  return (
    <h3 className="px-3 mt-4 mb-2 text-[11px] font-bold text-slate-400 uppercase tracking-wider select-none">
      {title}
    </h3>
  );
}

function MenuItem({ icon, label, active, onClick, collapsed = false }) {
  return (
    <button
      onClick={onClick}
      className={`group relative flex items-center w-full py-2.5 my-0.5 rounded-xl text-sm transition-all duration-200 ${
        collapsed ? 'justify-center px-2' : 'px-3 gap-3'
      } ${
        active
          ? "bg-amber-50/80 text-amber-700 font-semibold"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      {active && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-amber-600 rounded-r-md" />
      )}
      
      <div className={`${active ? 'text-amber-600' : 'text-slate-500 group-hover:text-slate-800'} transition-colors`}>
        {icon}
      </div>

      {!collapsed && <span className="truncate">{label}</span>}

      {collapsed && (
        <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 shadow-xl border border-slate-700 flex items-center">
          {label}
          <div className="absolute top-1/2 -left-1 -translate-y-1/2 border-[5px] border-transparent border-r-slate-800" />
        </div>
      )}
    </button>
  );
}

export default function Sidebar({ active, onMenuClick, onExpandedChange }) {
  const { user } = useAuth();
  const role = user?.role;
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (onExpandedChange) onExpandedChange(isExpanded);
  }, []); 

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    if (onExpandedChange) onExpandedChange(newState);
  };

  return (
    <aside
      className={`flex flex-col justify-between bg-white fixed left-0 z-40 print:hidden transition-all duration-300 border-r border-slate-200 shadow-[2px_0_8px_rgba(0,0,0,0.02)]
        ${isExpanded ? 'w-64' : 'w-20'} top-20 h-[calc(100vh-80px)]`}
    >
      {/* Botão Flutuante com hover em amber-600 */}
      <button
        onClick={handleToggle}
        className="absolute -right-3 top-4 bg-white border border-slate-200 rounded-full p-1 shadow-sm text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-all z-50 focus:outline-none"
        aria-label={isExpanded ? "Recolher menu" : "Expandir menu"}
      >
        {isExpanded ? <ChevronLeft size={16} strokeWidth={2.5} /> : <ChevronRight size={16} strokeWidth={2.5} />}
      </button>

      <nav className={`flex flex-col flex-1 pt-10 pb-4 ${isExpanded ? 'px-3 overflow-y-auto' : 'px-2'}`}>
        
        <MenuCategory title="Clínica" collapsed={!isExpanded} />
        <MenuItem icon={<Home size={20} />} label="Dashboard" active={active === "dashboard"} onClick={() => onMenuClick("dashboard")} collapsed={!isExpanded} />
        <MenuItem icon={<CalendarDays size={20} />} label="Agendar Paciente" active={active === "agendar"} onClick={() => onMenuClick("agendar")} collapsed={!isExpanded} />
        <MenuItem icon={<Users size={20} />} label="Pacientes" active={active === "pacientes"} onClick={() => onMenuClick("pacientes")} collapsed={!isExpanded} />

        {role !== "aluno" && (
          <>
            <MenuCategory title="Acadêmico" collapsed={!isExpanded} />
            <MenuItem icon={<UserRound size={20} />} label="Alunos" active={active === "alunos"} onClick={() => onMenuClick("alunos")} collapsed={!isExpanded} />
            <MenuItem icon={<BookOpen size={20} />} label="Disciplinas" active={active === "disciplinas"} onClick={() => onMenuClick("disciplinas")} collapsed={!isExpanded} />
            <MenuItem icon={<Mail size={20} />} label="Períodos" active={active === "periodos"} onClick={() => onMenuClick("periodos")} collapsed={!isExpanded} />

            <MenuCategory title="CME (Esterilização)" collapsed={!isExpanded} />
            <MenuItem icon={<PieChart size={20} />} label="Painel Estatístico" active={active === "dashboard-esterilizacao"} onClick={() => onMenuClick("dashboard-esterilizacao")} collapsed={!isExpanded} />
            <MenuItem icon={<Box size={20} />} label="Gestão de Caixas" active={active === "caixas"} onClick={() => onMenuClick("caixas")} collapsed={!isExpanded} />
            <MenuItem icon={<PackagePlus size={20} />} label="Esterilização" active={active === "esterilizacao"} onClick={() => onMenuClick("esterilizacao")} collapsed={!isExpanded} />
          </>
        )}
      </nav>

      {/* Base: Configurações e Ajuda */}
      <div className={`flex flex-col border-t border-slate-100 pt-3 pb-6 ${isExpanded ? 'px-3' : 'px-2'}`}>
        {['recepcao', 'admin', 'administrativo'].includes(role) && (
          <MenuItem icon={<Settings size={20} />} label="Ajustes do Sistema" active={active === "ajustes"} onClick={() => onMenuClick("ajustes")} collapsed={!isExpanded} />
        )}
        <MenuItem icon={<HelpCircle size={20} />} label="Central de Ajuda" active={active === "ajuda"} onClick={() => onMenuClick("ajuda")} collapsed={!isExpanded} />
      </div>
    </aside>
  );
}
